import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  FileText, 
  Plus, 
  Calendar, 
  MapPin, 
  Zap, 
  AlertTriangle,
  CheckCircle,
  Clock,
  Search,
  Filter,
  Download
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/utils/supabase';
import { cn } from '@/utils/cn';
import {
  calculateClasseEnergetiquePure,
  calculateClasseClimat,
  getClasseFinaleOfficielle
} from '@/utils/dpe-projections';

interface DPEAnalysis {
  id: string;
  numero_dpe?: string;
  adresse_ban?: string;
  nom_commune_ban?: string;
  code_postal_ban?: number;
  energy_class?: string;
  climate_class?: string;
  primary_energy_consumption?: number;
  greenhouse_gas_emissions?: number;
  conso_5_usages_par_m2_ep?: number;
  emission_ges_5_usages_par_m2?: number;
  living_area?: number;
  status: string;
  created_at: string;
  updated_at: string;
}

export const AnalysisListPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [analyses, setAnalyses] = useState<DPEAnalysis[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Load analyses
  useEffect(() => {
    const loadAnalyses = async () => {
      if (!user?.id) return;

      try {
        setLoading(true);
        setError(null);

        const { data, error: fetchError } = await supabase
          .from('dpe_data')
          .select(`
            id,
            numero_dpe,
            adresse_ban,
            nom_commune_ban,
            code_postal_ban,
            energy_class,
            climate_class,
            primary_energy_consumption,
            greenhouse_gas_emissions,
            conso_5_usages_par_m2_ep,
            emission_ges_5_usages_par_m2,
            living_area,
            status,
            created_at,
            updated_at
          `)
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (fetchError) {
          throw fetchError;
        }

        setAnalyses(data || []);
      } catch (err: any) {
        console.error('Error loading analyses:', err);
        setError(err.message || 'Erreur lors du chargement des analyses');
      } finally {
        setLoading(false);
      }
    };

    loadAnalyses();
  }, [user?.id]);

  // Filter analyses
  const filteredAnalyses = analyses.filter(analysis => {
    const matchesSearch = !searchTerm || 
      analysis.numero_dpe?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      analysis.adresse_ban?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      analysis.nom_commune_ban?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || analysis.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Get status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge variant="success">Terminée</Badge>;
      case 'draft':
      case 'extracting':
      case 'analyzing':
        return <Badge variant="info">En cours</Badge>;
      case 'error':
        return <Badge variant="danger">Erreur</Badge>;
      default:
        return <Badge variant="default">{status}</Badge>;
    }
  };

  // Get status icon
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'draft':
      case 'extracting':
      case 'analyzing':
        return <Clock className="w-5 h-5 text-blue-500" />;
      case 'error':
        return <AlertTriangle className="w-5 h-5 text-red-500" />;
      default:
        return <FileText className="w-5 h-5 text-gray-500" />;
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get display address
  const getDisplayAddress = (analysis: DPEAnalysis) => {
    if (analysis.adresse_ban) {
      return analysis.adresse_ban;
    }
    
    const parts = [];
    if (analysis.nom_commune_ban) parts.push(analysis.nom_commune_ban);
    if (analysis.code_postal_ban) parts.push(analysis.code_postal_ban.toString());
    
    return parts.length > 0 ? parts.join(', ') : 'Adresse non disponible';
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <LoadingSpinner size="lg" text="Chargement de vos analyses..." />
      </div>
    );
  }

  return (
    <div>
      <Helmet>
        <title>Mes analyses DPE - DPEWizard</title>
        <meta name="description" content="Consultez l'historique de toutes vos analyses DPE, leurs statuts et accédez rapidement à vos résultats." />
      </Helmet>
      
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Mes analyses DPE
          </h1>
          <p className="text-gray-600">
            Historique de toutes vos analyses DPE
          </p>
        </div>
        
        <Button
          variant="primary"
          onClick={() => navigate('/analysis/new')}
          icon={<Plus className="w-4 h-4 mr-2" />}
        >
          Nouvelle analyse
        </Button>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <Input
            placeholder="Rechercher par numéro DPE, adresse ou ville..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            leftIcon={<Search className="w-4 h-4" />}
          />
        </div>
        
        <div className="sm:w-48">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">Tous les statuts</option>
            <option value="completed">Terminées</option>
            <option value="draft">En cours</option>
            <option value="error">Erreurs</option>
          </select>
        </div>
      </div>

      {error && (
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-3 text-red-600">
              <AlertTriangle className="w-5 h-5 flex-shrink-0" />
              <p>{error}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {filteredAnalyses.length > 0 ? (
        <div className="space-y-4">
          {filteredAnalyses.map((analysis) => (
            <Card 
              key={analysis.id} 
              hover 
              className="cursor-pointer transition-all duration-200"
              onClick={() => {
                // Navigate directly to results for completed analyses
                if (analysis.status === 'completed') {
                  navigate(`/analysis/${analysis.id}/results`);
                } else {
                  // Navigate to analysis page for incomplete analyses
                  navigate(`/analysis/${analysis.id}`);
                }
              }}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      {getStatusIcon(analysis.status)}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900 truncate">
                          {analysis.numero_dpe || `Analyse ${analysis.id.substring(0, 8)}`}
                        </h3>
                        {getStatusBadge(analysis.status)}
                      </div>
                      
                      <div className="flex items-center text-sm text-gray-500 mb-2">
                        <MapPin className="w-4 h-4 mr-1" />
                        <span className="truncate">{getDisplayAddress(analysis)}</span>
                      </div>
                      
                      <div className="flex items-center text-sm text-gray-500">
                        <Calendar className="w-4 h-4 mr-1" />
                        <span>{formatDate(analysis.created_at)}</span>
                      </div>
                    </div>
                  </div>
                  
                  {analysis.status === 'completed' && (() => {
                    const conso = analysis.primary_energy_consumption || analysis.conso_5_usages_par_m2_ep || 0;
                    const ges = analysis.greenhouse_gas_emissions || analysis.emission_ges_5_usages_par_m2 || 0;

                    const classeEnergie = calculateClasseEnergetiquePure(conso, analysis.living_area);
                    const classeClimat = calculateClasseClimat(ges);
                    const classeFinale = getClasseFinaleOfficielle(classeEnergie, classeClimat);

                    return (
                      <div className="flex items-center space-x-6">
                        <div className="text-center">
                          <div className="text-sm text-gray-500 mb-1">Classe énergétique</div>
                          <Badge
                            energyClass={classeEnergie as any}
                            size="lg"
                            isWorst={classeEnergie === classeFinale}
                          />
                        </div>

                        <div className="text-center">
                          <div className="text-sm text-gray-500 mb-1">Consommation</div>
                          <div className="text-sm font-medium text-gray-900">
                            {conso} kWh/m²/an
                          </div>
                        </div>

                        <div className="text-center">
                          <div className="text-sm text-gray-500 mb-1">Classe climat</div>
                          <Badge
                            energyClass={classeClimat as any}
                            size="lg"
                            isWorst={classeClimat === classeFinale}
                          />
                        </div>

                        <div className="text-center">
                          <div className="text-sm text-gray-500 mb-1">Émissions GES</div>
                          <div className="text-sm font-medium text-gray-900">
                            {ges} kgCO₂/m²/an
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-8 text-center">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {searchTerm || statusFilter !== 'all' 
                ? 'Aucune analyse trouvée' 
                : 'Aucune analyse DPE'}
            </h3>
            <p className="text-gray-600 mb-6">
              {searchTerm || statusFilter !== 'all'
                ? 'Aucune analyse ne correspond à vos critères de recherche.'
                : 'Vous n\'avez pas encore effectué d\'analyse DPE. Commencez dès maintenant !'}
            </p>
            <Button
              variant="primary"
              onClick={() => navigate('/analysis/new')}
              icon={<Plus className="w-4 h-4 mr-2" />}
            >
              Nouvelle analyse
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Summary stats */}
      {analyses.length > 0 && (
        <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-gray-900 mb-1">
                {analyses.length}
              </div>
              <div className="text-sm text-gray-500">Total analyses</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-green-600 mb-1">
                {analyses.filter(a => a.status === 'completed').length}
              </div>
              <div className="text-sm text-gray-500">Terminées</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-blue-600 mb-1">
                {analyses.filter(a => ['draft', 'extracting', 'analyzing'].includes(a.status)).length}
              </div>
              <div className="text-sm text-gray-500">En cours</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-red-600 mb-1">
                {analyses.filter(a => a.status === 'error').length}
              </div>
              <div className="text-sm text-gray-500">Erreurs</div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AnalysisListPage;